// Fiche d'un collecteur : état, approbations (quatre yeux), clés, sources liées, journal de sécurité (curseur opaque) et
// actions d'administration. Pour une demande en attente, AUCUNE empreinte, clé ni identifiant de clé n'est affiché (O63).
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { CollecteurDetail, EntreeJournalCollecteur, RevocationVue } from '../api/types';
import { AlerteNomSuspect, Attendre, Erreur, Etiquette, EtiquetteEtatCollecteur, IdCourt } from '../composants/communs';
import { useEkera } from '../contexte';
import {
  detailLisible,
  EXPLICATION_ETAT_COLLECTEUR,
  groupesDeQuatre,
  identiteAvecId,
  identiteLisible,
  sujetSecond,
  LIBELLE_ACTION_JOURNAL_COLLECTEUR,
  nomCollecteur,
  SANS_CLE_VISIBLE,
} from '../domaine/collecteurs';
import { dateFr } from '../domaine/format';
import { lienVers } from '../routeur';
import { Approbation, Arret, LierSource, Refus, Revocation } from './CollecteurActions';

export function FicheCollecteur({ id }: { id: string }) {
  const { client } = useEkera();
  const d = useQuery({ queryKey: ['collecteurs', id], queryFn: () => client.collecteur(id) });
  return (
    <>
      <p>
        <a href={lienVers('collecteurs')}>← Tous les collecteurs</a>
      </p>
      <Attendre etats={[d]} quoi="du collecteur">
        {() => d.data && <Contenu key={d.data.id} c={d.data} />}
      </Attendre>
    </>
  );
}

function Contenu({ c }: { c: CollecteurDetail }) {
  const sansCle = SANS_CLE_VISIBLE.includes(c.etat);
  // O77 : le nom proposé est une donnée du collecteur, non vérifiée : jamais le titre (« Demande du … » tant qu'il n'est pas approuvé)
  const nom = nomCollecteur(c);
  return (
    <>
      <section className="panneau" aria-labelledby="t-identite">
        <div className="panneau-tete">
          <h2 id="t-identite">{nom}</h2>
          <EtiquetteEtatCollecteur etat={c.etat} />
        </div>
        <p className="note">{EXPLICATION_ETAT_COLLECTEUR[c.etat]}</p>
        <dl className="paires">
          <dt>Identifiant</dt>
          <dd>{c.id}</dd>
          <dt>Compte lié</dt>
          <dd>{c.sujet_emetteur ? `${c.sujet} (émetteur : ${c.sujet_emetteur})` : c.sujet}</dd>
          <dt>Demandé le</dt>
          <dd>{dateFr(c.cree_le)}</dd>
          <dt>Dernière activité</dt>
          <dd>{c.derniere_activite ? dateFr(c.derniere_activite) : 'Aucune requête signée acceptée'}</dd>
          <dt>Sources liées</dt>
          <dd>{c.sources}</dd>
          <dt>Refus de signature (24 h)</dt>
          <dd>
            {c.refus_24h}
            {c.refus_24h > 0 && <span className="note"> · voir le journal de sécurité ci-dessous</span>}
          </dd>
          {!sansCle && c.empreinte_courte && (
            <>
              <dt>Empreinte courte de la clé active</dt>
              <dd>{groupesDeQuatre(c.empreinte_courte)}</dd>
            </>
          )}
          {!sansCle && c.cle_depuis && (
            <>
              <dt>Clé active depuis</dt>
              <dd>
                {dateFr(c.cle_depuis)} {c.cle_a_renouveler && <Etiquette ton="attention">À renouveler (plus de 12 mois)</Etiquette>}
              </dd>
            </>
          )}
        </dl>
        {c.nom === null && c.nom_propose && <p className="note">Nom proposé (non vérifié) : {c.nom_propose}</p>}
        {c.etat === 'revoque' && c.revocation && <Revoque r={c.revocation} />}
        {c.approuve_par.length > 0 && c.etat !== 'en_attente' && (
          <p className="note">
            Approuvé par {c.approuve_par.map((a) => `${identiteAvecId(a)} (${dateFr(a.le)})`).join(' et ')}.
            {c.approuve_par.map((a) => (
              <AlerteNomSuspect key={`${a.par}-${a.le}`} x={a} />
            ))}
          </p>
        )}
      </section>

      {c.etat === 'en_attente' && <Approbation c={c} />}
      {(c.etat === 'en_attente' || c.etat === 'expiree') && <Refus c={c} />}
      {(c.etat === 'actif' || c.etat === 'desactive') && <Arret c={c} />}
      {c.etat === 'actif' && <LierSource c={c} />}
      <SourcesLiees c={c} />
      {(c.etat === 'actif' || c.etat === 'desactive') && <Revocation c={c} />}
      <Journal c={c} />
    </>
  );
}

function Revoque({ r }: { r: RevocationVue }) {
  const auteur = r.par_nom ?? r.par ?? null;
  return (
    <div className="encadre attention" role="status">
      <p>
        <b>Révoqué le {dateFr(r.le)}.</b>
        {auteur && (
          <>
            {' '}
            Par {auteur}
            <IdCourt x={r} />.
          </>
        )}{' '}
        Motif : {r.motif}.{' '}
        {r.compromis_depuis
          ? `Les lectures déposées depuis le ${dateFr(r.compromis_depuis)} sont marquées suspectes et ignorées (jamais effacées).`
          : ''}
      </p>
      <AlerteNomSuspect x={r} />
    </div>
  );
}

function SourcesLiees({ c }: { c: CollecteurDetail }) {
  const { cid } = useEkera();
  return (
    <section className="panneau" aria-labelledby="t-liees">
      <h2 id="t-liees">Sources liées ({c.sources_liees.length})</h2>
      {c.sources_liees.length === 0 ? (
        <p className="note">
          {c.etat === 'revoque' ? 'Les sources ont été déliées à la révocation.' : 'Aucune source n’est liée à ce collecteur : il n’a rien à lire.'}
        </p>
      ) : (
        <ul className="liste-points">
          {c.sources_liees.map((s) => (
            <li key={`${s.carte}-${s.source}`}>
              {s.carte === cid ? <a href={lienVers('sources', { source: s.source })}>{s.nom}</a> : s.nom}
              <small>
                Carte {s.carte} · {s.actif ? 'source active' : 'source désactivée'}
                {c.etat === 'desactive' ? ' · non servie : le collecteur est désactivé' : ''}
              </small>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Journal({ c }: { c: CollecteurDetail }) {
  const { client } = useEkera();
  const [plus, setPlus] = useState<EntreeJournalCollecteur[]>([]);
  const [suivant, setSuivant] = useState<string | null>(c.journal_suivant ?? null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [charge, setCharge] = useState(false);
  // la fiche a été relue (action faite) : on repart de la première page
  useEffect(() => {
    setPlus([]);
    setSuivant(c.journal_suivant ?? null);
  }, [c]);
  async function pagePrecedente() {
    if (!suivant) return;
    setCharge(true);
    setErreur(null);
    try {
      const p = await client.collecteur(c.id, { avant: suivant });
      setPlus((x) => [...x, ...p.journal]);
      setSuivant(p.journal_suivant ?? null);
    } catch (e) {
      setErreur(e);
    } finally {
      setCharge(false);
    }
  }
  const lignes = [...c.journal, ...plus];
  return (
    <section className="panneau" aria-labelledby="t-journal">
      <h2 id="t-journal">Journal de sécurité</h2>
      <p className="note">
        Demandes, approbations, activations, arrêts, révocations et refus de signature. Le journal ne s’efface pas.
        {SANS_CLE_VISIBLE.includes(c.etat) ? ' Les clés et empreintes d’une demande jamais activée n’y figurent pas.' : ''}
      </p>
      {lignes.length === 0 ? (
        <p className="note">Aucune entrée.</p>
      ) : (
        <div className="tableau-defile">
          <table>
            <caption className="sr">Journal de sécurité du collecteur, du plus récent au plus ancien</caption>
            <thead>
              <tr>
                <th scope="col">N°</th>
                <th scope="col">Date</th>
                <th scope="col">Événement</th>
                <th scope="col">Par</th>
                <th scope="col">Détail</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((e) => (
                <tr key={e.rang}>
                  <td className="num">{e.rang}</td>
                  <td>{dateFr(e.le)}</td>
                  <td>{LIBELLE_ACTION_JOURNAL_COLLECTEUR[e.action] ?? e.action}</td>
                  <td>
                    {identiteLisible(e)}
                    <IdCourt x={e} />
                    {sujetSecond(e) && <small style={{ display: 'block' }}>{sujetSecond(e)}</small>}
                    <AlerteNomSuspect x={e} />
                  </td>
                  <td>
                    {detailLisible(e.detail).map((x) => (
                      <span key={x.cle} style={{ display: 'block' }}>
                        {x.libelle} : {x.valeur}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {erreur != null && <Erreur erreur={erreur} titre="Impossible de lire la suite du journal." />}
      {suivant && (
        <p>
          <button type="button" className="btn" disabled={charge} onClick={() => void pagePrecedente()}>
            Entrées plus anciennes
          </button>
        </p>
      )}
    </section>
  );
}
